import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { 
  UserPlus, ClipboardCheck, FileSpreadsheet, Download, Upload, 
  Trash2, Eye, FileText, Loader2, CheckCircle, XCircle, 
  Calendar, Clock, User, Phone, Building, Truck, Check, X,
  AlertTriangle, UserCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import api from '../services/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecruitmentPage = () => {
  const [activeTab, setActiveTab] = useState('assessments');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // Data
  const [assessments, setAssessments] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [passedAssessments, setPassedAssessments] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [trailers, setTrailers] = useState([]);
  const [stats, setStats] = useState(null);
  
  // Dialogs
  const [showAssessmentDialog, setShowAssessmentDialog] = useState(false);
  const [showChecklistDialog, setShowChecklistDialog] = useState(false);
  const [showDocumentDialog, setShowDocumentDialog] = useState(false);
  const [showResultDialog, setShowResultDialog] = useState(false);
  const [editingAssessment, setEditingAssessment] = useState(null);
  const [editingChecklist, setEditingChecklist] = useState(null);
  const [viewingDocuments, setViewingDocuments] = useState(null);
  const [settingResultFor, setSettingResultFor] = useState(null);
  
  // Assessment Form
  const [assessmentForm, setAssessmentForm] = useState({
    assessment_date: new Date().toISOString().split('T')[0],
    assessment_time: '08:00',
    candidate_name: '',
    candidate_contact: '',
    assessor_driver_id: '',
    manager_name: '',
    company_site: '',
    vehicle_registration: '',
    trailer_registration: '',
    odometer_reading: '',
    notes: ''
  });
  
  // Checklist Form
  const [checklistForm, setChecklistForm] = useState({
    assessment_id: '',
    drivers_license_verified: { completed: false, explanation: '' },
    criminal_check_completed: { completed: false, explanation: '' },
    references_contacted: { completed: false, explanation: '' },
    medical_examination: { completed: false, explanation: '' },
    driver_practical_test_done: { completed: false, explanation: '' },
    all_required_documents_received: { completed: false, explanation: '' },
    personal_information: { completed: false, explanation: '' },
    contract_of_employment_signed: { completed: false, explanation: '' },
    truck_driver_sop_signed: { completed: false, explanation: '' },
    list_of_policies_handed: { completed: false, explanation: '' },
    driver_number: '',
    ppe_issued: { completed: false, explanation: '' },
    notes: ''
  });
  
  // Document upload
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('completed_assessment_form');
  const fileInputRef = React.useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = async () => {
    try {
      setLoading(true);
      
      const [assessmentsRes, checklistsRes, passedRes, driversRes, vehiclesRes, trailersRes, statsRes] = await Promise.all([
        api.get('/recruitment/assessments'),
        api.get('/recruitment/checklists'),
        api.get('/recruitment/passed-assessments'),
        api.get('/drivers'),
        api.get('/vehicles'),
        api.get('/trailers'),
        api.get('/recruitment/stats')
      ]);
      
      setAssessments(assessmentsRes.data || []);
      setChecklists(checklistsRes.data || []);
      setPassedAssessments(passedRes.data || []);
      setDrivers(driversRes.data || []);
      setVehicles(vehiclesRes.data || []);
      setTrailers(trailersRes.data || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getDriverName = (driverId) => {
    if (!driverId) return '-';
    const driver = drivers.find(d => d.id === driverId);
    if (!driver) return '-';
    return `${driver.first_name || driver.user?.first_name || ''} ${driver.last_name || driver.user?.last_name || ''}`.trim() || driver.employee_number || '-';
  };

  const resetAssessmentForm = () => {
    setAssessmentForm({
      assessment_date: new Date().toISOString().split('T')[0],
      assessment_time: '08:00',
      candidate_name: '',
      candidate_contact: '',
      assessor_driver_id: '',
      manager_name: '',
      company_site: '',
      vehicle_registration: '',
      trailer_registration: '',
      odometer_reading: '',
      notes: ''
    });
    setEditingAssessment(null);
  };

  const resetChecklistForm = () => {
    setChecklistForm({
      assessment_id: '',
      drivers_license_verified: { completed: false, explanation: '' },
      criminal_check_completed: { completed: false, explanation: '' },
      references_contacted: { completed: false, explanation: '' },
      medical_examination: { completed: false, explanation: '' },
      driver_practical_test_done: { completed: false, explanation: '' },
      all_required_documents_received: { completed: false, explanation: '' },
      personal_information: { completed: false, explanation: '' },
      contract_of_employment_signed: { completed: false, explanation: '' },
      truck_driver_sop_signed: { completed: false, explanation: '' },
      list_of_policies_handed: { completed: false, explanation: '' },
      driver_number: '',
      ppe_issued: { completed: false, explanation: '' },
      notes: ''
    });
    setEditingChecklist(null);
  };

  // ========== ASSESSMENT HANDLERS ==========

  const handleSubmitAssessment = async () => {
    if (!assessmentForm.candidate_name || !assessmentForm.assessor_driver_id) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      setSubmitting(true);
      
      const payload = {
        ...assessmentForm,
        odometer_reading: assessmentForm.odometer_reading ? parseFloat(assessmentForm.odometer_reading) : null
      };
      
      if (editingAssessment) {
        await api.put(
          `/recruitment/assessment/${editingAssessment.id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          payload
        );
        toast.success('Assessment updated');
      } else {
        await api.post(
          `/recruitment/assessment?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          payload
        );
        toast.success('Assessment scheduled');
      }
      
      setShowAssessmentDialog(false);
      resetAssessmentForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save assessment:', error);
      toast.error('Failed to save assessment');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditAssessment = (assessment) => {
    setEditingAssessment(assessment);
    setAssessmentForm({
      assessment_date: assessment.assessment_date || '',
      assessment_time: assessment.assessment_time || '08:00',
      candidate_name: assessment.candidate_name || '',
      candidate_contact: assessment.candidate_contact || '',
      assessor_driver_id: assessment.assessor_driver_id || '',
      manager_name: assessment.manager_name || '',
      company_site: assessment.company_site || '',
      vehicle_registration: assessment.vehicle_registration || '',
      trailer_registration: assessment.trailer_registration || '',
      odometer_reading: assessment.odometer_reading || '',
      notes: assessment.notes || ''
    });
    setShowAssessmentDialog(true);
  };

  const handleDeleteAssessment = async (id) => {
    if (!window.confirm('Are you sure you want to delete this assessment?')) return;
    
    try {
      await api.delete(`/recruitment/assessment/${id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`);
      toast.success('Assessment deleted');
      fetchData();
    } catch (error) {
      console.error('Failed to delete assessment:', error);
      toast.error('Failed to delete assessment');
    }
  };

  const handleSetResult = async (result) => {
    if (!settingResultFor) return;
    
    try {
      await api.put(
        `/recruitment/assessment/${settingResultFor.id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        { result }
      );
      toast.success(`Assessment marked as ${result.toUpperCase()}`);
      setShowResultDialog(false);
      setSettingResultFor(null);
      fetchData();
    } catch (error) {
      console.error('Failed to set result:', error);
      toast.error('Failed to set result');
    }
  };

  const handleDownloadForm = (assessmentId) => {
    const url = `${API_URL}/api/recruitment/assessment/${assessmentId}/download-form`;
    window.open(url, '_blank');
  };

  const handleDownloadTemplate = () => {
    const url = `${API_URL}/api/recruitment/download-form-template`;
    window.open(url, '_blank');
  };

  // ========== CHECKLIST HANDLERS ==========

  const handleSubmitChecklist = async () => {
    if (!checklistForm.assessment_id) {
      toast.error('Please select an assessment');
      return;
    }

    try {
      setSubmitting(true);
      
      if (editingChecklist) {
        await api.put(
          `/recruitment/checklist/${editingChecklist.id}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          checklistForm
        );
        toast.success('Checklist updated');
      } else {
        await api.post(
          `/recruitment/checklist?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
          checklistForm
        );
        toast.success('Checklist created');
      }
      
      setShowChecklistDialog(false);
      resetChecklistForm();
      fetchData();
    } catch (error) {
      console.error('Failed to save checklist:', error);
      toast.error(error.response?.data?.detail || 'Failed to save checklist');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditChecklist = (checklist) => {
    setEditingChecklist(checklist);
    setChecklistForm({
      assessment_id: checklist.assessment_id || '',
      drivers_license_verified: checklist.drivers_license_verified || { completed: false, explanation: '' },
      criminal_check_completed: checklist.criminal_check_completed || { completed: false, explanation: '' },
      references_contacted: checklist.references_contacted || { completed: false, explanation: '' },
      medical_examination: checklist.medical_examination || { completed: false, explanation: '' },
      driver_practical_test_done: checklist.driver_practical_test_done || { completed: false, explanation: '' },
      all_required_documents_received: checklist.all_required_documents_received || { completed: false, explanation: '' },
      personal_information: checklist.personal_information || { completed: false, explanation: '' },
      contract_of_employment_signed: checklist.contract_of_employment_signed || { completed: false, explanation: '' },
      truck_driver_sop_signed: checklist.truck_driver_sop_signed || { completed: false, explanation: '' },
      list_of_policies_handed: checklist.list_of_policies_handed || { completed: false, explanation: '' },
      driver_number: checklist.driver_number || '',
      ppe_issued: checklist.ppe_issued || { completed: false, explanation: '' },
      notes: checklist.notes || ''
    });
    setShowChecklistDialog(true);
  };

  const handleApproveChecklist = async (checklistId) => {
    if (!window.confirm('Are you sure you want to approve this checklist and create the driver?')) return;
    
    try {
      const res = await api.post(
        `/recruitment/checklist/${checklistId}/approve?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`
      );
      toast.success('Driver created successfully!');
      fetchData();
    } catch (error) {
      console.error('Failed to approve checklist:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve checklist');
    }
  };

  const handleRejectChecklist = async (checklistId) => {
    const reason = window.prompt('Please provide a reason for rejection:');
    if (!reason) return;
    
    try {
      await api.post(
        `/recruitment/checklist/${checklistId}/reject?reason=${encodeURIComponent(reason)}&user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`
      );
      toast.success('Checklist rejected');
      fetchData();
    } catch (error) {
      console.error('Failed to reject checklist:', error);
      toast.error('Failed to reject checklist');
    }
  };

  // ========== DOCUMENT HANDLERS ==========

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !viewingDocuments) return;
    
    try {
      setUploadingDoc(true);
      
      const formData = new FormData();
      formData.append('file', file);
      
      const { type, record } = viewingDocuments;
      let endpoint;
      
      if (type === 'assessment') {
        if (selectedDocType === 'completed_assessment_form') {
          endpoint = `/recruitment/assessment/${record.id}/upload-form`;
        } else {
          endpoint = `/recruitment/assessment/${record.id}/document?document_type=${selectedDocType}`;
        }
      } else {
        endpoint = `/recruitment/checklist/${record.id}/document?document_type=${selectedDocType}`;
      }
      
      await api.post(
        `${endpoint}&user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      toast.success('Document uploaded');
      fetchData();
      
      // Refresh viewing record
      if (type === 'assessment') {
        const res = await api.get('/recruitment/assessments');
        const updated = (res.data || []).find(r => r.id === record.id);
        if (updated) setViewingDocuments({ type, record: updated });
      } else {
        const res = await api.get('/recruitment/checklists');
        const updated = (res.data || []).find(r => r.id === record.id);
        if (updated) setViewingDocuments({ type, record: updated });
      }
    } catch (error) {
      console.error('Failed to upload document:', error);
      toast.error('Failed to upload document');
    } finally {
      setUploadingDoc(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleDownloadDocument = (type, recordId, docId) => {
    const endpoint = type === 'assessment' ? 'assessment' : 'checklist';
    const url = `${API_URL}/api/recruitment/${endpoint}/${recordId}/document/${docId}`;
    window.open(url, '_blank');
  };

  const handleDeleteDocument = async (type, recordId, docId) => {
    if (!window.confirm('Delete this document?')) return;
    
    try {
      await api.delete(
        `/recruitment/assessment/${recordId}/document/${docId}?user_id=${user.id}&user_name=${encodeURIComponent(user.name || user.email)}`
      );
      toast.success('Document deleted');
      fetchData();
      
      // Refresh viewing record
      const res = await api.get('/recruitment/assessments');
      const updated = (res.data || []).find(r => r.id === recordId);
      if (updated) setViewingDocuments({ type, record: updated });
    } catch (error) {
      console.error('Failed to delete document:', error);
      toast.error('Failed to delete document');
    }
  };

  const handleExport = (type) => {
    const url = `${API_URL}/api/recruitment/export/${type}`;
    window.open(url, '_blank');
  };

  const updateChecklistItem = (field, key, value) => {
    setChecklistForm(prev => ({
      ...prev,
      [field]: { ...prev[field], [key]: value }
    }));
  };

  const getStatusBadge = (status) => {
    const styles = {
      scheduled: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-gray-100 text-gray-800',
      pending: 'bg-amber-100 text-amber-800',
      approved: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return <Badge className={styles[status] || 'bg-gray-100'}>{status?.charAt(0).toUpperCase() + status?.slice(1)}</Badge>;
  };

  const getResultBadge = (result) => {
    if (!result) return <Badge className="bg-gray-100 text-gray-800">Pending</Badge>;
    if (result === 'pass') return <Badge className="bg-green-100 text-green-800">PASS</Badge>;
    return <Badge className="bg-red-100 text-red-800">FAIL</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="recruitment-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">RECRUITMENT</h1>
          <p className="text-slate-600">Manage driver assessments and onboarding</p>
        </div>
        <div className="flex gap-3 flex-wrap">
          <Button variant="outline" onClick={handleDownloadTemplate} className="gap-2">
            <Download className="w-4 h-4" />
            Assessment Form Template
          </Button>
          <Button 
            onClick={() => { resetAssessmentForm(); setShowAssessmentDialog(true); }}
            className="gap-2 bg-blue-600 hover:bg-blue-700"
            data-testid="new-assessment-btn"
          >
            <UserPlus className="w-4 h-4" />
            New Assessment
          </Button>
          <Button 
            onClick={() => { resetChecklistForm(); setShowChecklistDialog(true); }}
            className="gap-2 bg-green-600 hover:bg-green-700"
            disabled={passedAssessments.length === 0}
            data-testid="new-checklist-btn"
          >
            <ClipboardCheck className="w-4 h-4" />
            New Checklist
          </Button>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-blue-700 mb-1">
                <UserPlus className="w-4 h-4" />
                <span className="text-sm font-medium">Assessments</span>
              </div>
              <p className="text-2xl font-bold text-blue-900">{stats.assessments?.total || 0}</p>
              <p className="text-xs text-blue-600">{stats.assessments?.scheduled || 0} scheduled</p>
            </CardContent>
          </Card>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-green-700 mb-1">
                <CheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Passed</span>
              </div>
              <p className="text-2xl font-bold text-green-900">{stats.assessments?.passed || 0}</p>
              <p className="text-xs text-green-600">candidates</p>
            </CardContent>
          </Card>
          <Card className="border-red-200 bg-red-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-red-700 mb-1">
                <XCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Failed</span>
              </div>
              <p className="text-2xl font-bold text-red-900">{stats.assessments?.failed || 0}</p>
              <p className="text-xs text-red-600">candidates</p>
            </CardContent>
          </Card>
          <Card className="border-amber-200 bg-amber-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-amber-700 mb-1">
                <ClipboardCheck className="w-4 h-4" />
                <span className="text-sm font-medium">Checklists</span>
              </div>
              <p className="text-2xl font-bold text-amber-900">{stats.checklists?.total || 0}</p>
              <p className="text-xs text-amber-600">{stats.checklists?.pending || 0} pending</p>
            </CardContent>
          </Card>
          <Card className="border-emerald-200 bg-emerald-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-emerald-700 mb-1">
                <UserCheck className="w-4 h-4" />
                <span className="text-sm font-medium">Hired</span>
              </div>
              <p className="text-2xl font-bold text-emerald-900">{stats.checklists?.approved || 0}</p>
              <p className="text-xs text-emerald-600">new drivers</p>
            </CardContent>
          </Card>
          <Card className="border-gray-200 bg-gray-50">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 text-gray-700 mb-1">
                <AlertTriangle className="w-4 h-4" />
                <span className="text-sm font-medium">Rejected</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{stats.checklists?.rejected || 0}</p>
              <p className="text-xs text-gray-600">candidates</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="flex justify-between items-center">
          <TabsList>
            <TabsTrigger value="assessments" className="gap-2" data-testid="assessments-tab">
              <UserPlus className="w-4 h-4" />
              Assessments ({assessments.length})
            </TabsTrigger>
            <TabsTrigger value="checklists" className="gap-2" data-testid="checklists-tab">
              <ClipboardCheck className="w-4 h-4" />
              Appointment Checklists ({checklists.length})
            </TabsTrigger>
          </TabsList>
          <Button 
            variant="outline" 
            onClick={() => handleExport(activeTab)}
            className="gap-2"
            data-testid="export-btn"
          >
            <FileSpreadsheet className="w-4 h-4" />
            Export Excel
          </Button>
        </div>

        {/* Assessments Tab */}
        <TabsContent value="assessments">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-blue-50">
                    <TableHead>Date/Time</TableHead>
                    <TableHead>Candidate</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Assessor</TableHead>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Result</TableHead>
                    <TableHead>Form</TableHead>
                    <TableHead>Docs</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {assessments.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        No assessments found
                      </TableCell>
                    </TableRow>
                  ) : (
                    assessments.map((assessment) => (
                      <TableRow key={assessment.id}>
                        <TableCell>
                          <div className="font-mono text-sm">{assessment.assessment_date}</div>
                          <div className="text-xs text-slate-500">{assessment.assessment_time}</div>
                        </TableCell>
                        <TableCell className="font-medium">{assessment.candidate_name}</TableCell>
                        <TableCell className="text-sm">{assessment.candidate_contact}</TableCell>
                        <TableCell>{assessment.assessor_name || '-'}</TableCell>
                        <TableCell className="font-mono text-sm">{assessment.vehicle_registration}</TableCell>
                        <TableCell>{getStatusBadge(assessment.status)}</TableCell>
                        <TableCell>{getResultBadge(assessment.result)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadForm(assessment.id)}
                              title="Download Form"
                              className="text-blue-600"
                            >
                              <Download className="w-4 h-4" />
                            </Button>
                            {assessment.form_uploaded && (
                              <Badge className="bg-green-100 text-green-800 text-xs">Uploaded</Badge>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setViewingDocuments({ type: 'assessment', record: assessment })}
                            className="gap-1"
                          >
                            <FileText className="w-4 h-4" />
                            {assessment.documents?.length || 0}
                          </Button>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {assessment.status === 'scheduled' && !assessment.result && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => { setSettingResultFor(assessment); setShowResultDialog(true); }}
                                title="Set Result"
                                className="text-amber-600"
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditAssessment(assessment)}
                              title="Edit"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteAssessment(assessment.id)}
                              className="text-red-600"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Checklists Tab */}
        <TabsContent value="checklists">
          <Card>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-green-50">
                    <TableHead>Candidate</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Driver #</TableHead>
                    <TableHead>License</TableHead>
                    <TableHead>Criminal</TableHead>
                    <TableHead>Medical</TableHead>
                    <TableHead>Contract</TableHead>
                    <TableHead>PPE</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {checklists.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        No checklists found. Create one for a passed assessment.
                      </TableCell>
                    </TableRow>
                  ) : (
                    checklists.map((checklist) => (
                      <TableRow key={checklist.id}>
                        <TableCell className="font-medium">{checklist.candidate_name}</TableCell>
                        <TableCell className="text-sm">{checklist.candidate_contact}</TableCell>
                        <TableCell className="font-mono">{checklist.driver_number || '-'}</TableCell>
                        <TableCell>
                          {checklist.drivers_license_verified?.completed ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <X className="w-4 h-4 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell>
                          {checklist.criminal_check_completed?.completed ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <X className="w-4 h-4 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell>
                          {checklist.medical_examination?.completed ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <X className="w-4 h-4 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell>
                          {checklist.contract_of_employment_signed?.completed ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <X className="w-4 h-4 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell>
                          {checklist.ppe_issued?.completed ? (
                            <Check className="w-4 h-4 text-green-600" />
                          ) : (
                            <X className="w-4 h-4 text-red-600" />
                          )}
                        </TableCell>
                        <TableCell>{getStatusBadge(checklist.status)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            {checklist.status === 'pending' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleApproveChecklist(checklist.id)}
                                  title="Approve & Create Driver"
                                  className="text-green-600"
                                >
                                  <UserCheck className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleRejectChecklist(checklist.id)}
                                  title="Reject"
                                  className="text-red-600"
                                >
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditChecklist(checklist)}
                              title="View/Edit"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Assessment Dialog */}
      <Dialog open={showAssessmentDialog} onOpenChange={setShowAssessmentDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <UserPlus className="w-5 h-5" />
              {editingAssessment ? 'Edit Assessment' : 'Schedule New Assessment'}
            </DialogTitle>
            <DialogDescription>
              Schedule a physical driving assessment for a new driver candidate
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input
                type="date"
                value={assessmentForm.assessment_date}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, assessment_date: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Time *</Label>
              <Input
                type="time"
                value={assessmentForm.assessment_time}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, assessment_time: e.target.value })}
              />
            </div>
            
            <div className="space-y-2">
              <Label>Candidate Name *</Label>
              <Input
                value={assessmentForm.candidate_name}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, candidate_name: e.target.value })}
                placeholder="Full name of candidate"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Contact Details *</Label>
              <Input
                value={assessmentForm.candidate_contact}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, candidate_contact: e.target.value })}
                placeholder="Phone number or email"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Assessor (Existing Driver) *</Label>
              <Select 
                value={assessmentForm.assessor_driver_id || 'none'} 
                onValueChange={(v) => setAssessmentForm({ ...assessmentForm, assessor_driver_id: v === 'none' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select driver" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select driver</SelectItem>
                  {drivers.filter(d => d.status === 'active' || d.is_active).map(d => (
                    <SelectItem key={d.id} value={d.id}>{getDriverName(d.id)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Manager Name</Label>
              <Input
                value={assessmentForm.manager_name}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, manager_name: e.target.value })}
                placeholder="Manager overseeing assessment"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Company/Site</Label>
              <Input
                value={assessmentForm.company_site}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, company_site: e.target.value })}
                placeholder="Company or site name"
              />
            </div>
            
            <div className="space-y-2">
              <Label>Vehicle Registration *</Label>
              <Select 
                value={assessmentForm.vehicle_registration || 'none'} 
                onValueChange={(v) => setAssessmentForm({ ...assessmentForm, vehicle_registration: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="vehicle-registration-select">
                  <SelectValue placeholder="Select vehicle" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Select vehicle</SelectItem>
                  {vehicles
                    .filter(v => v.vehicle_type === 'Truck')
                    .map(v => (
                      <SelectItem key={v.id} value={v.registration_number}>{v.registration_number} {v.make ? `(${v.make})` : ''}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Trailer Registration</Label>
              <Select 
                value={assessmentForm.trailer_registration || 'none'} 
                onValueChange={(v) => setAssessmentForm({ ...assessmentForm, trailer_registration: v === 'none' ? '' : v })}
              >
                <SelectTrigger data-testid="trailer-registration-select">
                  <SelectValue placeholder="Select trailer (optional)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None</SelectItem>
                  {/* Show trailers from trailers collection */}
                  {trailers.map(t => (
                    <SelectItem key={t.id} value={t.registration_number}>{t.registration_number} {t.make ? `(${t.make})` : ''}</SelectItem>
                  ))}
                  {/* Also show vehicles marked as Trailer */}
                  {vehicles
                    .filter(v => v.vehicle_type === 'Trailer')
                    .map(v => (
                      <SelectItem key={v.id} value={v.registration_number}>{v.registration_number} {v.make ? `(${v.make})` : ''}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label>Odometer Reading</Label>
              <Input
                type="number"
                value={assessmentForm.odometer_reading}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, odometer_reading: e.target.value })}
                placeholder="Current odometer"
              />
            </div>
            
            <div className="col-span-2 space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={assessmentForm.notes}
                onChange={(e) => setAssessmentForm({ ...assessmentForm, notes: e.target.value })}
                placeholder="Any additional notes..."
                rows={2}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => { setShowAssessmentDialog(false); resetAssessmentForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitAssessment} disabled={submitting} className="bg-blue-600 hover:bg-blue-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingAssessment ? 'Update' : 'Schedule Assessment'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Result Dialog */}
      <Dialog open={showResultDialog} onOpenChange={setShowResultDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Set Assessment Result</DialogTitle>
            <DialogDescription>
              Record the result for {settingResultFor?.candidate_name}
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-4 justify-center mt-4">
            <Button 
              onClick={() => handleSetResult('pass')} 
              className="bg-green-600 hover:bg-green-700 gap-2 flex-1"
            >
              <CheckCircle className="w-5 h-5" />
              PASS
            </Button>
            <Button 
              onClick={() => handleSetResult('fail')} 
              className="bg-red-600 hover:bg-red-700 gap-2 flex-1"
            >
              <XCircle className="w-5 h-5" />
              FAIL
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Checklist Dialog */}
      <Dialog open={showChecklistDialog} onOpenChange={setShowChecklistDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <ClipboardCheck className="w-5 h-5" />
              {editingChecklist ? 'Edit Appointment Checklist' : 'New Appointment Checklist'}
            </DialogTitle>
            <DialogDescription>
              Complete the appointment checklist for a passed candidate
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            {!editingChecklist && (
              <div className="space-y-2">
                <Label>Select Passed Assessment *</Label>
                <Select 
                  value={checklistForm.assessment_id || 'none'} 
                  onValueChange={(v) => setChecklistForm({ ...checklistForm, assessment_id: v === 'none' ? '' : v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select assessment" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Select assessment</SelectItem>
                    {passedAssessments.map(a => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.candidate_name} - {a.assessment_date}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {editingChecklist && (
              <div className="p-3 bg-slate-100 rounded-lg">
                <p className="font-medium">{editingChecklist.candidate_name}</p>
                <p className="text-sm text-slate-600">{editingChecklist.candidate_contact}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label>Driver Number</Label>
              <Input
                value={checklistForm.driver_number}
                onChange={(e) => setChecklistForm({ ...checklistForm, driver_number: e.target.value })}
                placeholder="Assign driver number"
              />
            </div>

            <div className="border rounded-lg p-4 space-y-3">
              <h3 className="font-semibold text-slate-700">Checklist Items</h3>
              
              {[
                { field: 'drivers_license_verified', label: 'Drivers License Verified' },
                { field: 'criminal_check_completed', label: 'Criminal Check Completed' },
                { field: 'references_contacted', label: 'References Contacted' },
                { field: 'medical_examination', label: 'Medical Examination' },
                { field: 'driver_practical_test_done', label: 'Driver Practical Test Done' },
                { field: 'all_required_documents_received', label: 'All Required Documents Received' },
                { field: 'personal_information', label: 'Personal Information' },
                { field: 'contract_of_employment_signed', label: 'Contract of Employment Signed' },
                { field: 'truck_driver_sop_signed', label: 'Truck Driver SOP Signed' },
                { field: 'list_of_policies_handed', label: 'List of Policies Handed to Employee' },
                { field: 'ppe_issued', label: 'PPE Issued' }
              ].map(({ field, label }) => (
                <div key={field} className="flex items-start gap-4 p-2 rounded hover:bg-slate-50">
                  <div className="flex items-center gap-2 w-64">
                    <Checkbox
                      id={field}
                      checked={checklistForm[field]?.completed || false}
                      onCheckedChange={(checked) => updateChecklistItem(field, 'completed', checked)}
                    />
                    <Label htmlFor={field} className="cursor-pointer text-sm">{label}</Label>
                  </div>
                  {!checklistForm[field]?.completed && (
                    <Input
                      placeholder="Explanation if No..."
                      value={checklistForm[field]?.explanation || ''}
                      onChange={(e) => updateChecklistItem(field, 'explanation', e.target.value)}
                      className="flex-1 text-sm"
                    />
                  )}
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={checklistForm.notes}
                onChange={(e) => setChecklistForm({ ...checklistForm, notes: e.target.value })}
                placeholder="Additional notes..."
                rows={2}
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-3 mt-6">
            <Button variant="outline" onClick={() => { setShowChecklistDialog(false); resetChecklistForm(); }}>
              Cancel
            </Button>
            <Button onClick={handleSubmitChecklist} disabled={submitting} className="bg-green-600 hover:bg-green-700">
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingChecklist ? 'Update Checklist' : 'Create Checklist'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog */}
      <Dialog open={!!viewingDocuments} onOpenChange={() => setViewingDocuments(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Documents
            </DialogTitle>
          </DialogHeader>
          
          {viewingDocuments && (
            <div className="space-y-4 mt-4">
              <div className="p-4 border rounded-lg bg-slate-50">
                <Label className="text-sm font-medium">Upload Document</Label>
                <div className="flex gap-2 mt-2">
                  <Select value={selectedDocType} onValueChange={setSelectedDocType}>
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="completed_assessment_form">Completed Assessment Form</SelectItem>
                      <SelectItem value="id_document">ID Document</SelectItem>
                      <SelectItem value="license">License</SelectItem>
                      <SelectItem value="certificate">Certificate</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleDocumentUpload}
                    className="hidden"
                  />
                  <Button 
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingDoc}
                    className="gap-2"
                  >
                    {uploadingDoc ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                    Upload
                  </Button>
                </div>
              </div>
              
              <div className="space-y-2">
                {viewingDocuments.record.documents?.length === 0 ? (
                  <p className="text-center text-slate-500 py-4">No documents uploaded</p>
                ) : (
                  viewingDocuments.record.documents?.map((doc) => (
                    <div key={doc.id} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <FileText className="w-5 h-5 text-slate-400" />
                        <div>
                          <p className="text-sm font-medium truncate max-w-[200px]">{doc.filename}</p>
                          <p className="text-xs text-slate-500">
                            {doc.document_type} • {Math.round(doc.size / 1024)} KB
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDownloadDocument(
                            viewingDocuments.type,
                            viewingDocuments.record.id,
                            doc.id
                          )}
                          title="Download"
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteDocument(
                            viewingDocuments.type,
                            viewingDocuments.record.id,
                            doc.id
                          )}
                          className="text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RecruitmentPage;
